<template>
  <div class="">
    <m-column-filter ref="columnFilterModal" @apply="setColumnFilter"></m-column-filter>

    <files-viewer ref="viewer" />
    <div class="inventory-items-table lg">
      <scrolled-table :fix-last="true" @nextPage="$emit('nextPage')">
        <table class="h-table">
          <thead>
            <tr>
              <th v-for="column in visibleColumns" :key="column.id" :data-column="column.id">
                <div class="h-table__header">
                  <div
                    class="h-table__col-title"
                    :class="{
                      sortable: activeColumns.includes(column.id),
                      active: sortBy === column.id,
                    }"
                    @click="sort(column.id)"
                  >
                    {{ column.fullName }}
                  </div>

                  <div class="h-table__actions">
                    <c-button
                      v-b-popover.html="{
                        container: '#app',
                        trigger: 'hover',
                        content: column.description,
                        placement: 'bottom',
                      }"
                      icon="info"
                      size="iconic"
                    />

                    <template v-if="column.id in filters">
                      <filter-button
                        :id="`uniform-item-table-filter--` + column.unique"
                        :icon="{
                          icon: 'filter-column',
                          activeColor: 'blue',
                        }"
                        no-border
                        size="small"
                        :active="!!filters[column.id].value"
                        @click="configureFilter(column.id)"
                      />

                      <b-popover
                        id="h-table-filter-popover"
                        placement="top"
                        triggers="hover blur"
                        :target="`uniform-item-table-filter--` + column.unique"
                      >
                        {{ filters[column.id].value ? "Изменить фильтр" : "Применить фильтр" }}
                      </b-popover>
                    </template>
                  </div>
                </div>
              </th>

              <th width="70"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in items" :key="item.id">
              <td
                v-for="column in visibleColumns"
                :key="column.id"
                :data-column="column.id"
                :class="{
                  'expired': !item.isWrittenOff && item.isExpired,
                  'written-off': item.isWrittenOff,
                }"
              >
                <item-cell :item="item" :column="column.id" @showFiles="showItemFiles(item)"></item-cell>
              </td>
              <td
                :class="{
                  expired: !item.isWrittenOff && item.isExpired,
                }"
              >
                <item-actions
                  :item="item"
                  :can-remove="canRemove"
                  @edit="$emit('edit', item)"
                  @issue="$emit('issue', item)"
                  @withdraw="$emit('withdraw', item)"
                  @writeOff="$emit('writeOff', item)"
                  @restore="$emit('restore', item)"
                  @remove="$emit('remove', item)"
                  @showHistory="$emit('showHistory', item)"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </scrolled-table>

      <c-loader
        v-if="loading"
        class="inventory-items-table__loading"
        :active="loading"
        label="Данные загружаются"
        :height="200"
      ></c-loader>

      <div v-if="!loading && !items.length" class="inventory-items-table__empty">
        Ничего не найдено. Попробуйте изменить параметры поиска.
      </div>
    </div>

    <div class="inventory-items-table sm">
      <horizontal-table @nextPage="$emit('nextPage')">
        <table class="h-table">
          <tbody>
            <tr v-for="column in visibleColumns" :key="column.key" :data-column="column.key">
              <th>
                <div class="h-table__header">
                  <div
                    class="h-table__col-title"
                    :class="{
                      sortable: activeColumns.includes(column.id),
                      active: sortBy === column.id,
                    }"
                    @click="sort(column.id)"
                  >
                    {{ column.label }}
                  </div>

                  <div class="h-table__actions">
                    <c-button
                      v-b-popover.html="{
                        container: '#app',
                        trigger: 'hover',
                        content: column.description,
                        placement: 'bottom',
                      }"
                      icon="info"
                      size="iconic"
                    />

                    <template v-if="column.id in filters">
                      <filter-button
                        :id="`uniform-item-table-filter--` + column.unique"
                        :icon="{
                          icon: 'filter-column',
                          activeColor: 'blue',
                        }"
                        no-border
                        size="small"
                        :active="!!filters[column.id].value"
                        @click="configureFilter(column.id)"
                      />

                      <b-popover
                        id="h-table-filter-popover"
                        placement="top"
                        triggers="hover blur"
                        :target="`uniform-item-table-filter--` + column.unique"
                      >
                        {{ filters[column.id].value ? "Изменить фильтр" : "Применить фильтр" }}
                      </b-popover>
                    </template>
                  </div>
                </div>
              </th>
              <td
                v-for="item in items"
                :key="item.id"
                :class="{
                  'expired': !item.isWrittenOff && item.isExpired,
                  'written-off': item.isWrittenOff,
                }"
              >
                <item-cell :item="item" :column="column.id" @showFiles="showItemFiles(item)"></item-cell>
              </td>
            </tr>

            <tr>
              <th></th>
              <td
                v-for="item in items"
                :key="item.id"
                :class="{
                  expired: !item.isWrittenOff && item.isExpired,
                }"
              >
                <item-actions
                  :item="item"
                  :can-remove="canRemove"
                  @edit="edit(item)"
                  @issue="issue(item)"
                  @withdraw="withdraw(item)"
                  @writeOff="writeOff(item)"
                  @restore="restore(item)"
                  @remove="remove(item)"
                  @showHistory="showHistory(item)"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </horizontal-table>

      <c-loader
        v-if="loading"
        class="inventory-items-table__loading"
        :active="loading"
        label="Данные загружаются"
        :height="200"
      ></c-loader>

      <div v-if="!loading && !items.length" class="inventory-items-table__empty">
        Ничего не найдено. Попробуйте изменить параметры поиска.
      </div>
    </div>
  </div>
</template>

<script>
import ScrolledTable from "@/components/scrolled-table";
import HorizontalTable from "@/components/horizontal-table";
import CLoader from "@/components/loader";
import FilesViewer from "@/components/files-viewer";
import CButton from "@/components/controls2/Button";
import FilterButton from "@/components/controls/button";
import MColumnFilter from "@/components/modals/column-filter-modal";
import ItemCell from "./ItemCell";
import ItemActions from "./ItemActions";

export default {
  components: {
    ScrolledTable,
    HorizontalTable,
    CLoader,
    CButton,
    FilterButton,
    MColumnFilter,
    ItemCell,
    ItemActions,
    FilesViewer,
  },
  props: ["items", "canRemove", "filters", "sortBy", "loading", "page", "columns", "userColumns"],

  data() {
    return {
      activeColumns: [
        "category",
        "subcategory",
        "type",
        "guid",
        "name",
        "holder",
        // "expiryDate",
        "cost",
        "manufactureDate",
        "purchaseDate",
        "model",
        "purchasePlace",
        "issueDate",
      ],
    };
  },

  computed: {
    visibleColumns() {
      return this.columns.filter((column) => this.userColumns.includes(column.id));
    },
    categories() {
      return this.$store.state.uniformItems.catalog
        .filter((category) => {
          const { categories: subcategories, types } = category;
          if (types && types.length) return true;
          const hasNotEmptyCategory = subcategories.some((subcategory) => {
            if (!subcategory.types) return false;
            return subcategory.types.length > 0;
          });
          if (hasNotEmptyCategory) return true;
          return false;
        })
        .map((category) => ({
          value: category.id,
          label: category.name,
        }));
    },
  },

  methods: {
    edit(item) {
      this.$emit("edit", item);
    },

    issue(item) {
      this.$emit("issue", item);
    },

    withdraw(item) {
      this.$emit("withdraw", item);
    },

    writeOff(item) {
      this.$emit("writeOff", item);
    },

    remove(item) {
      this.$emit("remove", item);
    },

    restore(item) {
      this.$emit("restore", item);
    },

    showHistory(item) {
      this.$emit("showHistory", item);
    },

    nextPage() {
      this.$emit("nextPage");
    },

    showItemFiles(item) {
      this.$refs.viewer.open(item.files);
    },

    sort(columnName) {
      this.$emit("sort", columnName);
    },

    async getReferenceValues(columnName) {
      if (columnName === "category") return this.categories;

      return [];
    },

    configureFilter(columnName) {
      const filter = this.filters[columnName];

      this.$refs.columnFilterModal.open({
        name: columnName,
        type: filter.type,
        value: filter.value,
        getList: () => this.getReferenceValues(columnName),
      });
    },

    setColumnFilter({ name, value }) {
      console.log({ name, value });
      this.filters[name] = {
        type: this.filters[name].type,
        value,
      };

      this.$emit("filter");
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/vars";

.inventory-items-table {
  &.lg {
    @media screen and (max-width: 992px) {
    }

    .inventory-items-table__empty {
      color: #8e9cbb;
      margin-top: 30px;
      font-size: 15px;
    }

    .inventory-items-table__loading {
      margin-top: -12px;
    }
  }
  &.sm {
    display: none;
    position: relative;

    .inventory-items-table__loading {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      height: 100% !important;
      min-height: 200px;

      /deep/ {
        .loader-overlay {
          background: rgba(255, 255, 255, 0.5);
        }
      }
    }

    .inventory-items-table__empty {
      color: #8e9cbb;
      font-size: 15px;
      padding: 0 20px;
    }

    /deep/ {
      th {
        width: 120px;
        min-width: 120px;
        max-width: 120px;
      }
      td {
        width: calc(100vw - 120px);
        min-width: calc(100vw - 120px);
        max-width: calc(100vw - 120px);
      }
    }
  }

  .expired {
    background-color: #ffe6f2 !important;
  }
  .written-off * {
    opacity: 0.5;
  }

  @include onlyMobile {
    &.lg {
      display: none;
    }
    &.sm {
      display: block;
      margin-left: -21px;
      margin-right: -21px;
    }
  }
}
</style>
